/**
 * Contabilità IVA — dashboard previsionale + effettiva + delta.
 *
 * Regola de-dup: quando una bolla ha una fattura ricevuta collegata esce dal
 * previsionale ed entra nell'effettivo. Idem per attività fatturate.
 */
import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, Wallet, AlertCircle,
  ArrowUpDown, Calendar, Filter, ChevronRight, Calculator,
  FileSpreadsheet, FileText, Trash2, Pencil,
} from "lucide-react";
import api, { API_BASE } from "@/api";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "2-digit" }) : "—";

const TIPO_LABEL = {
  bolla: "Bolla / DDT",
  attivita: "Cronoprogramma",
  rapportino: "Rapportino",
  fattura_emessa: "Fattura emessa",
  fattura_ricevuta: "Fattura ricevuta",
};
const TIPO_COLOR = {
  bolla: "bg-orange-100 text-orange-800",
  attivita: "bg-blue-100 text-blue-800",
  rapportino: "bg-purple-100 text-purple-800",
  fattura_emessa: "bg-emerald-100 text-emerald-800",
  fattura_ricevuta: "bg-rose-100 text-rose-800",
};

export default function IvaContabilita() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("previsionale"); // previsionale | effettiva
  const [filtri, setFiltri] = useState({
    date_from: "",
    date_to: "",
    cantiere_id: "",
    fornitore: "",
    cliente: "",
    tipo_documento: "",
    include_rapportini: true,
  });

  // Endpoint di eliminazione per tipo documento — l'ID è sempre v.id
  const DELETE_ENDPOINTS = {
    bolla: (id) => `/bolle/${id}`,
    rapportino: (id) => `/rapportini/${id}`,
    attivita: (id) => `/cronoprogramma/${id}`,
    fattura_emessa: (id) => `/entita/fatture/${id}`,
    fattura_ricevuta: (id) => `/entita/fatture/${id}`,
  };
  // Route frontend per aprire la voce in modifica (l'utente atterra sulla pagina della sezione)
  const EDIT_ROUTES = {
    bolla: () => `/bolle`,
    rapportino: () => `/rapportini`,
    attivita: (v) => v.cantiere_id ? `/cantieri/${v.cantiere_id}?tab=crono` : `/programma-lavori`,
    fattura_emessa: () => `/contabilita`,
    fattura_ricevuta: () => `/contabilita`,
  };

  const deleteVoce = useMutation({
    mutationFn: async ({ tipo, id }) => {
      const path = DELETE_ENDPOINTS[tipo]?.(id);
      if (!path) throw new Error(`Tipo documento non supportato: ${tipo}`);
      const r = await api.delete(path);
      return r.data;
    },
    onSuccess: () => {
      toast.success("Voce eliminata — IVA ricalcolata");
      qc.invalidateQueries({ queryKey: ["iva-riepilogo"] });
      qc.invalidateQueries({ queryKey: ["programma-lavori-overview"] });
    },
    onError: (e) => {
      const status = e?.response?.status ? `HTTP ${e.response.status} · ` : "";
      toast.error(`Eliminazione fallita — ${status}${e?.response?.data?.detail || e.message || "Errore"}`);
    },
  });

  const { data: cantieri = [] } = useQuery({
    queryKey: ["cantieri-min"],
    queryFn: () => api.get("/cantieri").then((r) => r.data),
    staleTime: 5 * 60_000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["iva-riepilogo", filtri],
    queryFn: () => {
      const p = new URLSearchParams();
      Object.entries(filtri).forEach(([k, v]) => {
        if (v === "" || v == null) return;
        p.set(k, String(v));
      });
      return api.get(`/contabilita/iva/riepilogo?${p.toString()}`).then((r) => r.data);
    },
  });

  const set = (k, v) => setFiltri((f) => ({ ...f, [k]: v }));

  const prev = data?.previsionale;
  const eff = data?.effettiva;
  const voci = tab === "previsionale" ? (prev?.voci || []) : (eff?.voci || []);
  const perMese = tab === "previsionale" ? (prev?.per_mese || []) : (eff?.per_mese || []);

  return (
    <div className="space-y-6" data-testid="page-iva-contabilita">
      <div>
        <div className="overline">Contabilità</div>
        <h1 className="font-display text-3xl xl:text-4xl font-black flex items-center gap-2">
          <Wallet className="w-8 h-8 text-slate-500" /> IVA · Previsionale, Effettiva &amp; Delta
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Vista unica di quanta IVA è prevista dai documenti aperti (bolle non fatturate, attività Gantt, rapportini)
          e quanta è già effettiva. Le trasformazioni <b>non</b> vengono conteggiate due volte.
        </p>
      </div>

      {/* FILTRI */}
      <div className="bg-white border border-slate-200 p-3 flex flex-wrap gap-2 items-end text-xs">
        <Filter className="w-4 h-4 text-slate-500 mb-2" />
        <div>
          <label className="block text-slate-500 mb-0.5">Dal</label>
          <input type="date" value={filtri.date_from} onChange={(e) => set("date_from", e.target.value)}
                 data-testid="filter-iva-date-from"
                 className="px-2 py-1 border border-slate-300 rounded-sm" />
        </div>
        <div>
          <label className="block text-slate-500 mb-0.5">Al</label>
          <input type="date" value={filtri.date_to} onChange={(e) => set("date_to", e.target.value)}
                 className="px-2 py-1 border border-slate-300 rounded-sm" />
        </div>
        <div>
          <label className="block text-slate-500 mb-0.5">Cantiere</label>
          <select value={filtri.cantiere_id} onChange={(e) => set("cantiere_id", e.target.value)}
                  data-testid="filter-iva-cantiere"
                  className="px-2 py-1 border border-slate-300 rounded-sm">
            <option value="">Tutti</option>
            {cantieri.map((c) => <option key={c.cantiere_id} value={c.cantiere_id}>{c.nome}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-slate-500 mb-0.5">Fornitore</label>
          <input value={filtri.fornitore} onChange={(e) => set("fornitore", e.target.value)}
                 placeholder="es. Rossi" className="px-2 py-1 border border-slate-300 rounded-sm" />
        </div>
        <div>
          <label className="block text-slate-500 mb-0.5">Cliente</label>
          <input value={filtri.cliente} onChange={(e) => set("cliente", e.target.value)}
                 placeholder="es. Comune" className="px-2 py-1 border border-slate-300 rounded-sm" />
        </div>
        <div>
          <label className="block text-slate-500 mb-0.5">Tipo</label>
          <select value={filtri.tipo_documento} onChange={(e) => set("tipo_documento", e.target.value)}
                  className="px-2 py-1 border border-slate-300 rounded-sm">
            <option value="">Tutti</option>
            <option value="bolla">Bolle</option>
            <option value="attivita">Cronoprogramma</option>
            <option value="rapportino">Rapportini</option>
            <option value="fattura_emessa">Fatture emesse</option>
            <option value="fattura_ricevuta">Fatture ricevute</option>
          </select>
        </div>
        <label className="inline-flex items-center gap-1 mb-1 text-slate-600">
          <input type="checkbox" checked={filtri.include_rapportini}
                 onChange={(e) => set("include_rapportini", e.target.checked)} />
          Rapportini
        </label>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="IVA Previsionale (tot)" value={eur(prev?.totale)} tone="amber" icon={Calendar} />
        <Kpi label="IVA a debito (effettiva)" value={eur(eff?.iva_a_debito)} tone="rose" icon={TrendingUp} testId="kpi-iva-debito" />
        <Kpi label="IVA a credito (effettiva)" value={eur(eff?.iva_a_credito)} tone="emerald" icon={TrendingDown} testId="kpi-iva-credito" />
        <Kpi label="Delta IVA effettiva"
             value={eur(eff?.delta)}
             tone={(eff?.delta || 0) >= 0 ? "emerald" : "rose"}
             icon={ArrowUpDown}
             testId="kpi-iva-delta"
             sub={(eff?.delta || 0) >= 0 ? "A credito verso Erario" : "A debito verso Erario"} />
      </div>

      {/* Breakdown previsionale per fonte */}
      {prev && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <BreakBox label="Da bolle" value={prev.per_fonte?.bolle} />
          <BreakBox label="Da fatture in previsione" value={prev.per_fonte?.fatture} />
          <BreakBox label="Da cronoprogramma" value={prev.per_fonte?.attivita} />
          <BreakBox label="Da rapportini" value={prev.per_fonte?.rapportini} />
        </div>
      )}

      {/* TABS */}
      <div className="flex gap-1 border-b border-slate-200">
        <TabBtn active={tab === "previsionale"} onClick={() => setTab("previsionale")}
                testId="tab-iva-previsionale">
          Previsionale ({prev?.voci?.length || 0})
        </TabBtn>
        <TabBtn active={tab === "effettiva"} onClick={() => setTab("effettiva")}
                testId="tab-iva-effettiva">
          Effettiva ({eff?.voci?.length || 0})
        </TabBtn>
      </div>

      {/* per_mese */}
      {perMese.length > 0 && (
        <div className="bg-white border border-slate-200 p-3">
          <div className="overline mb-2">Aggregato per mese ({tab})</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-slate-500 bg-slate-50">
                <tr>
                  <th className="text-left p-2">Mese</th>
                  <th className="text-right p-2">IVA a debito</th>
                  <th className="text-right p-2">IVA a credito</th>
                  <th className="text-right p-2">{tab === "previsionale" ? "Totale" : "Delta"}</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {perMese.map((r) => (
                  <tr key={r.mese} className="border-t border-slate-100">
                    <td className="p-2 font-semibold">{r.mese}</td>
                    <td className="p-2 text-right">{eur(r.debito)}</td>
                    <td className="p-2 text-right">{eur(r.credito)}</td>
                    <td className="p-2 text-right font-bold">{eur(tab === "previsionale" ? r.totale : r.delta)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Voci dettaglio */}
      <div className="bg-white border border-slate-200">
        <div className="p-3 border-b border-slate-200 flex items-center justify-between">
          <div className="overline">Documenti ({voci.length})</div>
          {isLoading && <span className="text-xs text-slate-400 italic">Caricamento…</span>}
        </div>
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="text-slate-500 bg-slate-50 sticky top-0">
              <tr>
                <th className="text-left p-2">Tipo</th>
                <th className="text-left p-2">Cantiere</th>
                <th className="text-left p-2">Controparte</th>
                <th className="text-left p-2">Numero</th>
                <th className="text-left p-2">Data</th>
                <th className="text-left p-2">Prev. Fatt.</th>
                <th className="text-right p-2">Imponibile</th>
                <th className="text-right p-2">IVA %</th>
                <th className="text-right p-2">IVA €</th>
                <th className="text-right p-2">Totale</th>
                <th className="text-left p-2">Stato</th>
                <th className="text-right p-2">Azioni</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {voci.length === 0 && !isLoading && (
                <tr><td colSpan={12} className="p-8 text-center text-slate-400 italic">
                  Nessun documento con i filtri correnti.
                </td></tr>
              )}
              {voci.map((v) => (
                <tr key={`${v.tipo}-${v.id}`} className="border-t border-slate-100 hover:bg-slate-50"
                    data-testid={`iva-row-${v.tipo}-${v.id}`}>
                  <td className="p-2">
                    <span className={`px-1.5 py-0.5 rounded-sm text-[9px] uppercase font-bold ${TIPO_COLOR[v.tipo] || "bg-slate-100"}`}>
                      {TIPO_LABEL[v.tipo] || v.tipo}
                    </span>
                  </td>
                  <td className="p-2">
                    {v.cantiere_id ? (
                      <Link to={`/cantieri/${v.cantiere_id}`} className="hover:text-orange-700">
                        {v.cantiere_nome || "—"}
                      </Link>
                    ) : "—"}
                  </td>
                  <td className="p-2 truncate max-w-[180px]">{v.controparte || "—"}</td>
                  <td className="p-2 truncate max-w-[120px]">{v.numero || "—"}</td>
                  <td className="p-2">{fmtDate(v.data)}</td>
                  <td className="p-2">{fmtDate(v.data_prevista_fatturazione)}</td>
                  <td className="p-2 text-right">{eur(v.imponibile)}</td>
                  <td className="p-2 text-right">{v.aliquota_iva}%</td>
                  <td className={`p-2 text-right font-bold ${v.verso === "credito" ? "text-emerald-700" : "text-rose-700"}`}>
                    {v.verso === "credito" ? "+" : "−"}{eur(v.importo_iva)}
                  </td>
                  <td className="p-2 text-right">{eur(v.totale)}</td>
                  <td className="p-2 text-[10px] uppercase text-slate-500">{v.stato}</td>
                  <td className="p-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      {EDIT_ROUTES[v.tipo] && (
                        <Link
                          to={EDIT_ROUTES[v.tipo](v)}
                          data-testid={`iva-edit-${v.tipo}-${v.id}`}
                          title={`Apri ${TIPO_LABEL[v.tipo] || v.tipo} per modificarla`}
                          className="p-1 rounded-sm border border-blue-300 hover:bg-blue-50 text-blue-700"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Link>
                      )}
                      {DELETE_ENDPOINTS[v.tipo] && (
                        <button
                          type="button"
                          onClick={() => {
                            const label = TIPO_LABEL[v.tipo] || v.tipo;
                            const nome = v.numero || v.controparte || v.id;
                            if (window.confirm(
                              `Eliminare definitivamente questa voce?\n\n` +
                              `${label} — ${nome}\n${fmtDate(v.data)}\n\n` +
                              `L'IVA (previsionale/effettiva) verrà ricalcolata automaticamente.`
                            )) {
                              deleteVoce.mutate({ tipo: v.tipo, id: v.id });
                            }
                          }}
                          disabled={deleteVoce.isPending}
                          data-testid={`iva-delete-${v.tipo}-${v.id}`}
                          title="Elimina definitivamente la voce"
                          className="p-1 rounded-sm border border-rose-300 hover:bg-rose-50 text-rose-700 disabled:opacity-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-[11px] text-slate-500 italic flex items-start gap-1">
        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <div>
          <b>Regola de-duplicazione</b>: appena una bolla viene collegata a una fattura ricevuta (o un'attività
          viene fatturata), il documento precedente esce automaticamente dal previsionale e viene mostrato solo
          nell'effettivo. Nessun doppio conteggio.
        </div>
      </div>

      <CostiVariabiliPanel />
      <SimulatoreBepPanel />
      <ExportButtons filtri={filtri} />
    </div>
  );
}

/**
 * Bottoni Export: 4 pulsanti (IVA XLSX/PDF · BEP XLSX/PDF) che scaricano il
 * riepilogo pronto per il commercialista rispettando i filtri correnti.
 */
function ExportButtons({ filtri }) {
  const build = () => {
    const p = new URLSearchParams();
    ["date_from", "date_to", "cantiere_id"].forEach((k) => {
      if (filtri[k]) p.set(k, filtri[k]);
    });
    return p.toString();
  };
  const open = (path) => {
    const q = build();
    const url = `${API_BASE}${path}${q ? (path.includes("?") ? "&" : "?") + q : ""}`;
    const w = window.open(url, "_blank", "noopener,noreferrer");
    if (w) w.opener = null;
  };

  return (
    <div className="mt-6 bg-white border border-slate-200 p-4" data-testid="panel-export-iva">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="overline">Export</div>
          <h3 className="font-display font-bold text-base">Consegna al commercialista</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Esporta i dati filtrati in Excel o PDF. I filtri (date, cantiere) sono applicati agli export.
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => open("/contabilita/export/iva/xlsx")}
          data-testid="btn-export-iva-xlsx"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-sm"
        >
          <FileSpreadsheet className="w-3.5 h-3.5" /> IVA · Excel
        </button>
        <button
          onClick={() => open("/contabilita/export/iva/pdf")}
          data-testid="btn-export-iva-pdf"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-rose-300 text-rose-800 hover:bg-rose-50 rounded-sm"
        >
          <FileText className="w-3.5 h-3.5" /> IVA · PDF
        </button>
        <div className="border-l border-slate-200 mx-1" />
        <button
          onClick={() => open("/contabilita/export/bep/xlsx")}
          data-testid="btn-export-bep-xlsx"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-sm"
        >
          <FileSpreadsheet className="w-3.5 h-3.5" /> BEP · Excel
        </button>
        <button
          onClick={() => open("/contabilita/export/bep/pdf")}
          data-testid="btn-export-bep-pdf"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-rose-300 text-rose-800 hover:bg-rose-50 rounded-sm"
        >
          <FileText className="w-3.5 h-3.5" /> BEP · PDF
        </button>
      </div>
    </div>
  );
}

/**
 * Sezione 7-9 — Stima di Fatturato → X → Costi di Gestione → Punto di Pareggio.
 * L'utente inserisce Stima di Fatturato, il sistema calcola tutto usando ✱.
 */
function SimulatoreBepPanel() {
  const [stima, setStima] = useState(1000000);
  const [overridePerc, setOverridePerc] = useState("");
  const params = new URLSearchParams();
  params.set("stima_fatturato", String(stima || 0));
  if (overridePerc !== "") params.set("percentuale_costi_variabili", String(overridePerc));
  const { data, isLoading } = useQuery({
    queryKey: ["simulatore-bep", stima, overridePerc],
    queryFn: () => api.get(`/contabilita/iva/simulatore-bep?${params.toString()}`).then((r) => r.data),
    keepPreviousData: true,
  });

  const perc = data?.percentuale_costi_variabili_usata;
  const cvs = data?.costi_variabili_stimati || 0;
  const x = data?.x || 0;
  const cg = data?.costi_di_gestione || 0;
  const risultato = data?.risultato_bep || 0;
  const dett = data?.dettaglio_costi_gestione || {};
  const stato = data?.stato;

  const statoCls = stato === "utile" ? "text-emerald-700 bg-emerald-50 border-emerald-300"
                 : stato === "perdita" ? "text-rose-700 bg-rose-50 border-rose-300"
                 : "text-slate-700 bg-slate-100 border-slate-300";

  return (
    <div className="mt-6 bg-white border-2 border-slate-800 shadow-brutal" data-testid="panel-simulatore-bep">
      <div className="p-4 border-b border-slate-200 bg-slate-800 text-white">
        <div className="overline text-orange-300">Simulatore</div>
        <h2 className="font-display font-bold text-xl flex items-center gap-2">
          <Calculator className="w-5 h-5 text-orange-400" />
          Stima Fatturato · Costi di Gestione · Punto di Pareggio
        </h2>
        <p className="text-[11px] text-slate-300 mt-0.5">
          Simulazione automatica: <b>Stima Fatturato − Costi Variabili Stimati (✱) − Costi di Gestione = Risultato BEP</b>.
        </p>
      </div>

      {/* INPUT */}
      <div className="p-4 grid md:grid-cols-2 gap-4 border-b border-slate-200 bg-slate-50">
        <div>
          <label className="text-xs font-semibold text-slate-700 block mb-1">
            Stima di Fatturato (annuo) €
          </label>
          <input
            type="number" min="0" step="1000" value={stima}
            onChange={(e) => setStima(Number(e.target.value) || 0)}
            data-testid="input-stima-fatturato"
            className="w-full px-3 py-2 border-2 border-slate-300 rounded-sm font-mono text-lg font-bold"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700 block mb-1">
            ✱ % Costi Variabili (default: {perc != null ? `${perc}%` : "n/d"})
          </label>
          <input
            type="number" min="0" max="100" step="0.5"
            placeholder={perc != null ? `${perc}` : "n/d — inserisci override"}
            value={overridePerc}
            onChange={(e) => setOverridePerc(e.target.value)}
            data-testid="input-override-perc"
            className="w-full px-3 py-2 border border-slate-300 rounded-sm font-mono"
          />
          <div className="text-[10px] text-slate-500 mt-0.5">
            Lascia vuoto per usare ✱ Ethos calcolata dai cantieri.
          </div>
        </div>
      </div>

      {/* CALCOLO STEP BY STEP */}
      <div className="p-4 space-y-2 text-sm">
        {isLoading && <div className="text-slate-400 italic">Calcolo…</div>}
        {!isLoading && (
          <>
            <BepRow label="Stima di Fatturato" value={eur(stima)} bold />
            <BepRow
              label={<>− ✱ Costi Variabili Stimati <span className="text-[10px] opacity-70">({perc != null ? `${perc.toFixed(2)}%` : "n/d"})</span></>}
              value={`− ${eur(cvs)}`}
              tone="rose"
            />
            <div className="my-2 border-t-2 border-dashed border-slate-300" />
            <BepRow label="= X (disponibile dopo costi variabili)" value={eur(x)} bold tone="blue" testId="bep-x-value" />
            <BepRow
              label={<>− Costi di Gestione <span className="text-[10px] opacity-70">(fissi + base lavoratori)</span></>}
              value={`− ${eur(cg)}`}
              tone="rose"
            />
            <div className="pl-4 text-[10px] text-slate-500 space-y-0.5">
              <div>• Costi di gestione annualizzati: {eur(dett.costi_fissi_annualizzati)}</div>
              <div>• Base lavoratori annualizzata: {eur(dett.base_lavoratori_annualizzata)} ({dett.ore_annue_lavoratore_usate} h/anno)</div>
            </div>
            <div className="my-2 border-t-2 border-slate-800" />
            <div className={`p-3 border-2 rounded-sm ${statoCls}`} data-testid="bep-risultato">
              <div className="flex items-center justify-between">
                <div className="font-bold uppercase text-xs tracking-widest">
                  {stato === "utile" ? "✓ Utile: fatturato supera i costi" :
                   stato === "perdita" ? "✗ Perdita: fatturato non copre i costi" :
                   "= Punto di pareggio"}
                </div>
                <div className="font-display font-black text-2xl">
                  {risultato >= 0 ? "+" : ""}{eur(risultato)}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function BepRow({ label, value, bold, tone, testId }) {
  const toneCls = tone === "rose" ? "text-rose-700" :
                  tone === "blue" ? "text-blue-800" :
                  tone === "emerald" ? "text-emerald-700" : "";
  return (
    <div className={`flex items-center justify-between font-mono ${bold ? "font-bold" : ""} ${toneCls}`} data-testid={testId}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

/**
 * Sezione 5-6 — Percentuale costi variabili per cantiere + * Stima Ethos.
 * Formula per cantiere: (Σ Fornitori / Σ Fatture emesse) × 100
 * * Stima Ethos = media aritmetica delle percentuali di tutti i cantieri con
 * fatturato > 0 (aggiornata automaticamente al variare dei documenti).
 */
function CostiVariabiliPanel() {
  const { data, isLoading } = useQuery({
    queryKey: ["costi-variabili-perc"],
    queryFn: () => api.get("/contabilita/iva/costi-variabili").then((r) => r.data),
    refetchInterval: 120_000,
  });

  const cantieri = data?.cantieri || [];
  const stimaEthos = data?.stima_percentuale_costi_variabili_ethos;

  return (
    <div className="mt-6 bg-white border-2 border-orange-300 shadow-brutal" data-testid="panel-costi-variabili-cantiere">
      <div className="p-4 border-b border-orange-200 bg-orange-50/60 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-orange-700">Costi variabili</div>
          <h2 className="font-display font-bold text-xl flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-orange-600" />
            Incidenza fornitori per cantiere
          </h2>
          <p className="text-[11px] text-slate-500 mt-0.5">
            % = (Σ Fornitori / Σ Fatture emesse del cantiere) × 100. Somma Fornitori = bolle non fatturate + fatture ricevute.
          </p>
        </div>
        <div className="text-right">
          <div className="overline text-orange-700">✱ Stima % Costi Variabili Ethos</div>
          <div className="font-display font-black text-4xl text-orange-700" data-testid="stima-ethos-percentuale">
            {stimaEthos != null ? `${stimaEthos.toFixed(2)}%` : "—"}
          </div>
          <div className="text-[10px] text-slate-500">
            media di {data?.cantieri_considerati_per_stima || 0} cantieri con fatturato &gt; 0
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-slate-500 bg-slate-50">
            <tr>
              <th className="text-left p-2">Cantiere</th>
              <th className="text-left p-2">Cliente</th>
              <th className="text-right p-2">Σ Fornitori</th>
              <th className="text-right p-2">Σ Fatture</th>
              <th className="text-right p-2">% Costi variabili</th>
              <th className="text-left p-2">Stato</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {isLoading && (
              <tr><td colSpan={6} className="p-6 text-center text-slate-400 italic">Caricamento…</td></tr>
            )}
            {!isLoading && cantieri.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-slate-400 italic">Nessun cantiere con fatturato attivo.</td></tr>
            )}
            {cantieri.map((r) => {
              const p = r.percentuale_costi_variabili;
              const cls = p == null ? "text-slate-400" :
                          p < 50 ? "text-emerald-700" :
                          p < 75 ? "text-amber-700" :
                                   "text-rose-700";
              return (
                <tr key={r.cantiere_id} className="border-t border-slate-100 hover:bg-slate-50"
                    data-testid={`row-costo-var-${r.cantiere_id}`}>
                  <td className="p-2">
                    <Link to={`/cantieri/${r.cantiere_id}`} className="font-semibold hover:text-orange-700">
                      {r.nome || "—"}
                    </Link>
                  </td>
                  <td className="p-2 text-slate-600 truncate max-w-[180px]">{r.cliente || "—"}</td>
                  <td className="p-2 text-right">{eur(r.somma_fornitori)}</td>
                  <td className="p-2 text-right">{eur(r.somma_fatture)}</td>
                  <td className={`p-2 text-right font-bold text-base ${cls}`}>
                    {p != null ? `${p.toFixed(2)}%` : "—"}
                  </td>
                  <td className="p-2 text-[10px] uppercase text-slate-500">{r.stato}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-300 bg-slate-50 font-mono">
              <td className="p-2 font-bold uppercase text-[10px]">Totale</td>
              <td></td>
              <td className="p-2 text-right font-bold">{eur(data?.totale_fornitori)}</td>
              <td className="p-2 text-right font-bold">{eur(data?.totale_fatture_emesse)}</td>
              <td className="p-2 text-right font-bold text-orange-700 text-lg">
                ✱ {stimaEthos != null ? `${stimaEthos.toFixed(2)}%` : "—"}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="p-3 border-t border-slate-100 text-[10px] text-slate-500 italic flex items-start gap-1">
        <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
        <div>
          La <b>✱ Stima % Costi Variabili Ethos</b> viene aggiornata automaticamente ogni volta
          che vengono aggiunte bolle, fatture emesse o fatture ricevute. Può essere usata dal
          sistema come percentuale aziendale stimata per calcoli previsionali su nuovi cantieri.
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone = "slate", icon: Icon, testId, sub }) {
  const tones = {
    slate: "bg-white border-slate-200 text-slate-800",
    rose: "bg-rose-50 border-rose-300 text-rose-800",
    amber: "bg-amber-50 border-amber-300 text-amber-800",
    emerald: "bg-emerald-50 border-emerald-300 text-emerald-800",
  };
  return (
    <div className={`p-4 border shadow-brutal ${tones[tone] || tones.slate}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">{label}</div>
        {Icon && <Icon className="w-4 h-4 opacity-60" />}
      </div>
      <div className="font-display font-black text-2xl mt-1">{value}</div>
      {sub && <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}

function BreakBox({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 p-2">
      <div className="text-[9px] uppercase font-bold text-slate-500 tracking-widest">{label}</div>
      <div className="font-display font-bold text-sm mt-0.5">{eur(value)}</div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
            className={`px-3 py-2 text-xs font-bold uppercase tracking-widest border-b-2 ${
              active ? "border-orange-600 text-orange-700" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>
      {children}
    </button>
  );
}
